import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'
import { useRequests } from '../../requests/useRequests'
import { useConversations } from '../../hooks/useConversations'
import { useMessages } from '../../hooks/useMessages'
import { useSearch } from '../../hooks/useSearch'
import ConversationsList from './ConversationsList'
import NewConversationForm from './NewConversationForm'
import MessagesList from './MessagesList'
import MessageForm from './MessageForm'
import UserAvatar from './UserAvatar'
import { SideMenu } from '../Header/SiteMenu'
import AppNavBar from '../Header/AppNavBar'

const Messages = () => {
  const { username, id } = useSelector((state: State) => state.user)
  const requests = useRequests()
  const [showNewMessageForm, setShowNewMessageForm] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  // Hooki
  const {
    conversations,
    loading: conversationsLoading,
    selectedConversation,
    setSelectedConversation,
    updateUnreadCount,
    updateLastMessage,
    fetchConversations
  } = useConversations()

  const {
    messages,
    setMessages,
    loading: messagesLoading,
    newMessage,
    setNewMessage,
    handleDownloadAttachment
  } = useMessages(selectedConversation, updateUnreadCount)

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    handleSearch
  } = useSearch()

  // Wybór konwersacji z dodatkowymi zabezpieczeniami
  const handleSelectConversation = (convId: number | null) => {
    if (convId === selectedConversation && convId !== null) return

    setSelectedConversation(convId)
    if (convId !== null && showNewMessageForm) {
      setShowNewMessageForm(false)
    }
  }

  // Posortowane konwersacje od najnowszych
  const sortedConversations = React.useMemo(() => {
    return [...conversations].sort((a, b) => {
      const dateA = a.lastMessageTime ? new Date(a.lastMessageTime) : new Date(0)
      const dateB = b.lastMessageTime ? new Date(b.lastMessageTime) : new Date(0)
      return dateB.getTime() - dateA.getTime()
    })
  }, [conversations])

  // Obsługa wysyłania nowej wiadomości
  const handleSendMessage = async (e: React.FormEvent, attachmentsToSend: File[]) => {
    e.preventDefault()

    if (!newMessage.trim() && attachmentsToSend.length === 0) return
    if (selectedConversation === null) return

    const selectedConv = conversations.find(c => c.id === selectedConversation)
    if (selectedConv == null) return

    setLoading(true)

    try {
      // Przygotowanie załączników - przesyłamy pliki na serwer
      const preparedAttachments = await Promise.all(
        attachmentsToSend.map(async (file) => {
          try {
            // Prześlij plik do backendu
            const uploadResponse = await requests.uploadFile(file, 'messages')

            return {
              name: file.name,
              type: file.type,
              size: file.size,
              url: uploadResponse.data.fileKey
            }
          } catch (error) {
            console.error('Błąd podczas przesyłania załącznika:', error)
            throw error
          }
        })
      )

      // Wysłanie wiadomości
      const messageData = {
        content: newMessage,
        recipientId: selectedConv.participantId,
        conversationId: selectedConversation,
        attachments: preparedAttachments.length > 0 ? preparedAttachments : undefined
      }

      const response = await requests.sendMessage(messageData)

      // Dodanie nowej wiadomości do listy
      const newMsg = {
        id: response.data.id,
        senderId: id || 0,
        senderName: username || 'Użytkownik',
        recipientId: selectedConv.participantId,
        recipientName: selectedConv.participantName,
        content: newMessage,
        timestamp: new Date().toISOString(),
        attachments: preparedAttachments
      }

      setMessages(prevMessages => [...prevMessages, newMsg] as any)

      // Aktualizacja ostatniej wiadomości w konwersacji
      updateLastMessage(
        selectedConversation,
        newMessage,
        preparedAttachments.length > 0
      )

      // Resetuj pole wiadomości
      setNewMessage('')
    } catch (err) {
      console.error('Błąd podczas wysyłania wiadomości:', err)
    } finally {
      setLoading(false)
    }
  }

  const currentConversationDetails = useMemo(() => {
    if (!selectedConversation) return null
    return conversations.find(c => c.id === selectedConversation)
  }, [selectedConversation, conversations])

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />
      <div className='tw-flex tw-h-[calc(100vh-56px)]'>
        <SideMenu />
        <section className='tw-flex-1 tw-p-3 md:tw-p-4 lg:tw-p-6 tw-overflow-y-auto tw-bg-[#f7fafd]'>
          <div className='tw-flex tw-justify-end tw-mb-4'>
            <button
              className='tw-flex tw-items-center tw-justify-center tw-gap-x-1.5 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-bg-green hover:tw-bg-opacity-90 tw-text-white tw-px-3 tw-py-2 tw-text-sm md:tw-px-4 md:tw-py-2.5 md:tw-text-base focus:tw-outline-none focus:tw-bg-green'
              onClick={() => {
                // Logika kliknięcia przycisku "Nowa konwersacja"
                const shouldOpenForm = !showNewMessageForm
                setShowNewMessageForm(shouldOpenForm)
                if (shouldOpenForm) {
                  handleSelectConversation(null) // Odznacz konwersację, gdy otwierasz formularz
                }
              }}
            >
              <i className='fa fa-plus' /> Nowa konwersacja
            </button>
          </div>

          {/* --- Układ Mobilny (<md) --- */}
          <div className='md:tw-hidden'>
            {showNewMessageForm ? (
              <NewConversationForm
                onCancel={() => setShowNewMessageForm(false)}
                onSuccess={(conversationId) => {
                  handleSelectConversation(conversationId)
                  setShowNewMessageForm(false)
                  fetchConversations()
                }}
                searchQuery={searchQuery}
                onSearchQueryChange={(query) => {
                  setSearchQuery(query)
                  if (query.length >= 3) handleSearch(query)
                }}
                searchResults={searchResults}
              />
            ) : selectedConversation !== null && (currentConversationDetails != null) ? (
              <>
                <div className='tw-mb-3'>
                  <button
                    onClick={() => handleSelectConversation(null)}
                    className='tw-flex tw-items-center tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-indigo-600 hover:tw-text-indigo-800 hover:tw-bg-indigo-50 tw-rounded-md'
                  >
                    <i className='fa fa-arrow-left tw-mr-2' aria-hidden='true' />
                    Konwersacje
                  </button>
                </div>
                <div className='tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm tw-flex tw-flex-col tw-h-[calc(100vh-15rem)]'> {/* Dostosuj wysokość dla mobile */}
                  <div className='tw-flex tw-items-center tw-p-3 md:tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50/75 tw-rounded-t-lg'> {/* Zmniejszona nieprzezroczystość tła dla subtelniejszego efektu, responsywny padding */}
                    <UserAvatar
                      name={currentConversationDetails.participantName || 'User'}
                      size={40} // Rozmiar awatara
                      id={currentConversationDetails.participantId}
                    />
                    <h4 className='tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-0 tw-ml-3 md:tw-ml-4'>
                      {currentConversationDetails.participantName}
                    </h4>
                  </div>

                  <div className='tw-flex-grow tw-overflow-y-auto'>
                    <MessagesList
                      messages={messages}
                      currentUserId={id}
                      onDownloadAttachment={handleDownloadAttachment}
                      loading={messagesLoading}
                    />
                  </div>
                  <div className='tw-border-t tw-border-gray-200'>
                    <MessageForm
                      newMessage={newMessage}
                      setNewMessage={setNewMessage}
                      onSendMessage={handleSendMessage}
                      loading={loading}
                    />
                  </div>
                </div>
              </>
            ) : (
              <ConversationsList
                conversations={sortedConversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                loading={conversationsLoading}
              />
            )}
          </div>

          {/* --- Układ Desktop (md+) --- */}
          <div className='tw-hidden md:tw-grid md:tw-grid-cols-12 md:tw-gap-x-4 lg:tw-gap-x-6'>
            {/* Kolumna 1: Lista konwersacji (zawsze widoczna na desktopie) */}
            <div className='md:tw-col-span-4 lg:tw-col-span-3'>
              <ConversationsList
                conversations={sortedConversations}
                selectedConversation={selectedConversation}
                onSelectConversation={handleSelectConversation}
                loading={conversationsLoading}
              />
            </div>

            {/* Kolumna 2: Formularz LUB Szczegóły konwersacji LUB Placeholder */}
            <div className='md:tw-col-span-8 lg:tw-col-span-9'>
              {showNewMessageForm ? (
                <NewConversationForm
                  onCancel={() => setShowNewMessageForm(false)}
                  onSuccess={(conversationId) => {
                    handleSelectConversation(conversationId)
                    setShowNewMessageForm(false)
                    fetchConversations()
                  }}
                  searchQuery={searchQuery}
                  onSearchQueryChange={(query) => {
                    setSearchQuery(query)
                    if (query.length >= 3) handleSearch(query)
                  }}
                  searchResults={searchResults}
                />
              ) : (currentConversationDetails != null) && selectedConversation !== null ? (
                <div className='tw-bg-white tw-border tw-border-gray-200 md:tw-rounded-lg tw-shadow-sm tw-flex tw-flex-col tw-h-[calc(100vh-12rem)] lg:tw-h-[calc(100vh-10rem)]'> {/* Dostosuj wysokość */}
                  <div className='tw-flex tw-items-center tw-p-3 md:tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50 md:tw-rounded-t-lg'>
                    <UserAvatar
                      name={currentConversationDetails.participantName || 'User'}
                      size={40}
                      id={currentConversationDetails.participantId}
                    />
                    <h4 className='tw-text-base md:tw-text-lg tw-font-semibold tw-mb-0 tw-ml-3'>
                      {currentConversationDetails.participantName}
                    </h4>
                  </div>
                  <div className='tw-flex-grow tw-overflow-y-auto'>
                    <MessagesList
                      messages={messages}
                      currentUserId={id}
                      onDownloadAttachment={handleDownloadAttachment}
                      loading={messagesLoading}
                    />
                  </div>
                  <div className='tw-border-t tw-border-gray-200'>
                    <MessageForm
                      newMessage={newMessage}
                      setNewMessage={setNewMessage}
                      onSendMessage={handleSendMessage}
                      loading={loading}
                    />
                  </div>
                </div>
              ) : (
                <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white md:tw-rounded-lg tw-shadow-sm tw-p-10 tw-h-full'>
                  <i className='fa fa-comments tw-text-4xl lg:tw-text-5xl tw-text-gray-300 tw-mb-5' />
                  <h3 className='tw-text-xl lg:tw-text-2xl tw-font-semibold tw-text-gray-700 tw-mb-2'>
                    Wybierz konwersację
                  </h3>
                  <p className='tw-text-sm lg:tw-text-base tw-text-gray-500'>
                    Aby wyświetlić wiadomości, wybierz konwersację z listy lub rozpocznij nową.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Messages
